import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Divider,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    IconButton,
    LinearProgress,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Alert,
    Chip,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2'; // Using the newer Grid2 component
import {
    ArrowBack,
    Close,
    Edit,
    Save,
    Cancel,
    PlayCircleOutline,
    Add as AddIcon,
    Delete as DeleteIcon,
    Analytics as AnalyticsIcon,
    ChevronLeft,
    ChevronRight,
    Today,
    CalendarMonth,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useCampaigns } from '../hooks/useCampaigns';
import { Campaign, AdVariation } from '../models/Campaign';
import ImageUpload from './ImageUpload';
import VideoUpload from './VideoUpload';
import { CampaignService } from '../services/firebase';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { VALID_CATEGORIES } from '../utils/standardizeCategories';
import AdDisplay from './AdDisplay';
import { AnalyticsService } from '../services/analytics';
import { Analytics } from '../models/Analytics';
import {
    LineChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    Line,
    ResponsiveContainer,
} from 'recharts';

// Helper function to format date
const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }).format(date);
};

// Detail Item component
const DetailItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {label}
        </Typography>
        {typeof value === 'string' ? (
            <Typography variant="body1">{value}</Typography>
        ) : (
            value
        )}
    </Box>
);

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`campaign-tabpanel-${index}`}
            aria-labelledby={`campaign-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const CampaignDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { fetchCampaignById } = useCampaigns();
    
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showImageFullscreen, setShowImageFullscreen] = useState(false);
    const [showVideoPlayer, setShowVideoPlayer] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedVariation, setSelectedVariation] = useState<string>('');
    const [unsavedChanges, setUnsavedChanges] = useState<{ [key: string]: Partial<AdVariation> }>({});
    const [isSaving, setIsSaving] = useState(false);
    const [analytics, setAnalytics] = useState<Analytics | null>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [analyticsError, setAnalyticsError] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState('daily');
    const [analyticsTarget, setAnalyticsTarget] = useState<string>('campaign'); // 'campaign' or variationId
    
    // New state for enhanced date navigation
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showDatePicker, setShowDatePicker] = useState(false);

    // State for delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [variationToDelete, setVariationToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Helper functions for date navigation
    const formatDisplayDate = (date: Date, range: string): string => {
        if (range === 'daily') {
            return date.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } else if (range === 'weekly') {
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            return `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        } else {
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long' 
            });
        }
    };

    const navigateDate = (direction: 'prev' | 'next') => {
        const newDate = new Date(selectedDate);
        if (timeRange === 'daily') {
            newDate.setDate(selectedDate.getDate() + (direction === 'next' ? 1 : -1));
        } else if (timeRange === 'weekly') {
            newDate.setDate(selectedDate.getDate() + (direction === 'next' ? 7 : -7));
        } else {
            newDate.setMonth(selectedDate.getMonth() + (direction === 'next' ? 1 : -1));
        }
        setSelectedDate(newDate);
    };

    const goToToday = () => {
        setSelectedDate(new Date());
    };

    const getDateKey = (date: Date, range: string): string => {
        if (range === 'daily') {
            return date.toISOString().split('T')[0];
        } else if (range === 'weekly') {
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay());
            return weekStart.toISOString().split('T')[0];
        } else {
            const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
            return monthStart.toISOString().split('T')[0];
        }
    };

    const getMetricsForSelectedPeriod = (metrics: any) => {
        if (!metrics) return null;
        
        const dateKey = getDateKey(selectedDate, timeRange);
        const source = timeRange === 'daily' ? metrics.dailyMetrics || {} :
                      timeRange === 'weekly' ? metrics.weeklyMetrics || {} :
                      metrics.monthlyMetrics || {};
        
        return source[dateKey] || { impressions: 0, clicks: 0, viewDuration: 0 };
    };
    
    useEffect(() => {
        const loadCampaign = async () => {
            if (!id) {
                setError('Campaign ID is missing');
                setLoading(false);
                return;
            }

            try {
                const campaignData = await fetchCampaignById(id);
                if (campaignData) {
                    setCampaign(campaignData);
                } else {
                    setError('Campaign not found');
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load campaign');
            } finally {
                setLoading(false);
            }
        };

        loadCampaign();
    }, [id, fetchCampaignById]);

    useEffect(() => {
        const loadAnalytics = async () => {
            if (!id) return;
            
            try {
                const data = await AnalyticsService.getAnalytics(id);
                setAnalytics(data);
            } catch (err) {
                setAnalyticsError('Failed to load analytics data');
            } finally {
                setAnalyticsLoading(false);
            }
        };

        loadAnalytics();
        // Refresh analytics every 30 seconds
        const interval = setInterval(loadAnalytics, 30000);
        return () => clearInterval(interval);
    }, [id]);

    const handleImageUpload = async (imageUrl: string) => {
        if (!campaign || !id) return;
        
        try {
            // Create a new variation or update the default one
            const variationId = Date.now().toString();
            const newVariation: AdVariation = {
                id: variationId,
                name: 'Default Image Variation',
                start_time: '00:00',
                end_time: '23:59',
                image_url: imageUrl,
                ad_type: 'image'
            };

            const updatedVariations = [...(campaign.ad_variations || []), newVariation];
            const updatedCampaign = {
                ...campaign,
                ad_variations: updatedVariations
            };
            
            // Update Firestore
            await CampaignService.updateCampaign(id, updatedCampaign);
            
            // Update local state
            setCampaign(updatedCampaign);
            
            // Select the new variation
            setSelectedVariation(variationId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update campaign');
        }
    };

    const handleVideoUpload = async (videoUrl: string) => {
        if (!campaign || !id) return;
        
        try {
            // Create a new variation or update the default one
            const variationId = Date.now().toString();
            const newVariation: AdVariation = {
                id: variationId,
                name: 'Default Video Variation',
                start_time: '00:00',
                end_time: '23:59',
                video_url: videoUrl,
                ad_type: 'video'
            };

            const updatedVariations = [...(campaign.ad_variations || []), newVariation];
            const updatedCampaign = {
                ...campaign,
                ad_variations: updatedVariations
            };
            
            // Update Firestore
            await CampaignService.updateCampaign(id, updatedCampaign);
            
            // Update local state
            setCampaign(updatedCampaign);
            
            // Select the new variation
            setSelectedVariation(variationId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update campaign');
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleTargetLinkClick = (url: string | undefined) => {
        if (!url) return;
        window.open(url, '_blank');
    };

    // Function to add a new ad variation
    const handleAddVariation = async () => {
        if (!campaign || !id) return;
        
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        
        const newVariation: AdVariation = {
            id: Date.now().toString(), // Simple ID generation
            name: 'New Ad Variation',
            start_time: '00:00',
            end_time: '23:59',
            start_date: todayStr,
            end_date: todayStr,
            image_url: '',
            video_url: ''
        };
        
        // Initialize or update ad_variations array
        const currentVariations = campaign.ad_variations || [];
        const updatedVariations = [...currentVariations, newVariation];
        
        try {
            const updatedCampaign = {
                ...campaign,
                ad_variations: updatedVariations
            };

            // Update Firestore
            await CampaignService.updateCampaign(id, updatedCampaign);
            
            // Update local state
            setCampaign(updatedCampaign);
            
            // Select the new variation
            setSelectedVariation(newVariation.id);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add variation');
        }
    };
    
    // Function to track changes without saving immediately
    const handleVariationChange = (variationId: string, updates: Partial<AdVariation>) => {
        setUnsavedChanges(prev => {
            const existingChanges = prev[variationId] || {};
            
            // Start with existing changes
            let newChanges: Partial<AdVariation> = { ...existingChanges };

            // Apply the new updates
            newChanges = {
                ...newChanges,
                ...updates
            };

            // Determine ad_type based on media presence
            if (newChanges.video_url) {
                newChanges.ad_type = 'video';
                newChanges.image_url = ''; // Clear image when video is present
            } else if (newChanges.image_url) {
                newChanges.ad_type = 'image';
                newChanges.video_url = ''; // Clear video when image is present
            } else {
                // If no media is present, delete the ad_type field instead of setting it to null/undefined
                delete newChanges.ad_type;
            }

            return {
                ...prev,
                [variationId]: newChanges
            };
        });
    };

    // Function to save variation changes
    const handleSaveVariation = async (variationId: string) => {
        if (!campaign || !id || !unsavedChanges[variationId]) return;

        setIsSaving(true);
        try {
            // Find the variation to update
            const updatedVariations = campaign.ad_variations.map(variation => {
                if (variation.id === variationId) {
                    const updatedVariation = { ...variation, ...unsavedChanges[variationId] };
                    // If there's no media, remove the ad_type field
                    if (!updatedVariation.video_url && !updatedVariation.image_url) {
                        delete updatedVariation.ad_type;
                    }
                    return updatedVariation;
                }
                return variation;
            });

            const updatedCampaign = {
                ...campaign,
                ad_variations: updatedVariations,
            };

            // Update Firestore
            await CampaignService.updateCampaign(id, updatedCampaign);

            // Update local state
            setCampaign(updatedCampaign);

            // Clear unsaved changes for this variation
            setUnsavedChanges(prev => {
                const newChanges = { ...prev };
                delete newChanges[variationId];
                return newChanges;
            });

            // Show success message
            console.log('Variation updated successfully');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save variation');
        } finally {
            setIsSaving(false);
        }
    };

    // Function to handle media upload in variation edit form
    const handleVariationMediaUpload = async (file: File, type: 'image' | 'video', variationId: string) => {
        if (!campaign || !id) return;

        try {
            let url;
            if (type === 'image') {
                url = await CampaignService.uploadImage(file, `${id}_${variationId}`);
                handleVariationChange(variationId, { 
                    image_url: url,
                    video_url: '',
                    ad_type: 'image'
                });
            } else {
                url = await CampaignService.uploadVideo(file, `${id}_${variationId}`, () => {});
                handleVariationChange(variationId, { 
                    video_url: url,
                    image_url: '',
                    ad_type: 'video'
                });
            }

            // Auto-save after upload
            await handleSaveVariation(variationId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to upload media');
        }
    };

    // Function to delete an ad variation
    const handleDeleteVariation = async (variationId: string) => {
        if (!campaign || !id) return;
        
        setVariationToDelete(variationId);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!campaign || !id || !variationToDelete) return;

        setDeleteLoading(true);
        try {
            // Remove the variation from the campaign's ad_variations array
            const updatedVariations = campaign.ad_variations.filter(
                variation => variation.id !== variationToDelete
            );

            const updatedCampaign = {
                ...campaign,
                ad_variations: updatedVariations
            };

            // Update Firestore
            await CampaignService.updateCampaign(id, updatedCampaign);
            
            // Delete analytics data for this variation
            await AnalyticsService.deleteVariationAnalytics(id, variationToDelete);
            
            // Update local state
            setCampaign(updatedCampaign);
            
            if (selectedVariation === variationToDelete) {
                setSelectedVariation('');
            }

            // Clear unsaved changes for the deleted variation
            setUnsavedChanges(prev => {
                const newChanges = { ...prev };
                delete newChanges[variationToDelete];
                return newChanges;
            });

            // Refresh analytics data to reflect the deletion
            try {
                const data = await AnalyticsService.getAnalytics(id);
                setAnalytics(data);
            } catch (err) {
                console.warn('Failed to refresh analytics after deletion');
            }

            console.log('Variation and its analytics deleted successfully');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete variation');
        } finally {
            setDeleteLoading(false);
            setDeleteDialogOpen(false);
            setVariationToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setVariationToDelete(null);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !campaign) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
                    Back to Campaigns
                </Button>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="error" variant="h6">
                        {error || 'Campaign not found'}
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
                    Back to Campaigns
                </Button>
                <Box>
                    {isEditing ? (
                        <>
                            <Button 
                                startIcon={<Cancel />} 
                                onClick={() => setIsEditing(false)} 
                                sx={{ mr: 1 }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="contained" 
                                startIcon={<Save />} 
                                onClick={() => setIsEditing(false)}
                            >
                                Save
                            </Button>
                        </>
                    ) : (
                        <Button 
                            variant="outlined" 
                            startIcon={<Edit />} 
                            onClick={() => setIsEditing(true)}
                        >
                            Edit
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        aria-label="campaign tabs"
                    >
                        <Tab 
                            icon={<Edit />} 
                            iconPosition="start" 
                            label="Details" 
                        />
                        <Tab 
                            icon={<PlayCircleOutline />} 
                            iconPosition="start" 
                            label="Ad Variations" 
                        />
                        <Tab 
                            icon={<AnalyticsIcon />} 
                            iconPosition="start" 
                            label="Analytics" 
                        />
                    </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h5" gutterBottom>
                            {campaign.centre_name || 'Unnamed Center'}
                        </Typography>
                        
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            {campaign.category && `${campaign.category} • `}
                            {campaign.city || 'Unknown Location'}
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Grid container spacing={3}>
                            {/* Location Information */}
                            <Grid xs={12} md={6}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                        Location Details
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <DetailItem label="City" value={campaign.city || 'N/A'} />
                                        <DetailItem label="Region" value={campaign.region || 'N/A'} />
                                        <DetailItem label="Country" value={campaign.country || 'N/A'} />
                                        {campaign.latitude && campaign.longitude && (
                                            <DetailItem 
                                                label="Coordinates" 
                                                value={`${campaign.latitude.toFixed(6)}, ${campaign.longitude.toFixed(6)}`} 
                                            />
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Business Information */}
                            <Grid xs={12} md={6}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                        Business Details
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <DetailItem label="Centre Name" value={campaign.centre_name || 'N/A'} />
                                        {isEditing ? (
                                            <FormControl fullWidth>
                                                <InputLabel>Category</InputLabel>
                                                <Select
                                                    value={campaign.category || ''}
                                                    onChange={async (e) => {
                                                        if (!campaign || !id) return;
                                                        try {
                                                            // Get the new category value
                                                            const categoryValue = e.target.value || undefined;
                                                            
                                                            // Get a reference to the campaign document
                                                            const campaignRef = doc(getFirestore(), 'campaigns', id);
                                                            
                                                            // Update only the category field directly
                                                            await updateDoc(campaignRef, {
                                                                category: categoryValue
                                                            });
                                                            
                                                            // Update local state
                                                            setCampaign({
                                                                ...campaign,
                                                                category: categoryValue
                                                            });
                                                        } catch (err) {
                                                            setError(err instanceof Error ? err.message : 'Failed to update category');
                                                        }
                                                    }}
                                                    label="Category"
                                                >
                                                    <MenuItem value="">None</MenuItem>
                                                    <MenuItem value={VALID_CATEGORIES.FRANCHISE}>{VALID_CATEGORIES.FRANCHISE}</MenuItem>
                                                    <MenuItem value={VALID_CATEGORIES.CORPORATE}>{VALID_CATEGORIES.CORPORATE}</MenuItem>
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <DetailItem label="Category" value={campaign.category || 'N/A'} />
                                        )}
                                        
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">
                                Ad Variations
                            </Typography>
                            <Button
                                startIcon={<AddIcon />}
                                variant="contained"
                                onClick={handleAddVariation}
                            >
                                Add Variation
                            </Button>
                        </Box>

                        {/* Grid of Variation Cards */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            {campaign.ad_variations?.map((variation) => (
                                <Grid xs={12} sm={6} md={4} key={variation.id}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            cursor: 'pointer',
                                            border: theme => selectedVariation === variation.id ? 
                                                `2px solid ${theme.palette.primary.main}` : '1px solid #ddd',
                                            '&:hover': {
                                                boxShadow: 3,
                                            },
                                            position: 'relative',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                        onClick={() => setSelectedVariation(variation.id)}
                                    >
                                        {/* Preview Image/Video */}
                                        <Box sx={{ 
                                            height: 140, 
                                            mb: 2, 
                                            bgcolor: 'grey.100',
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {/* Show unsaved media first, fall back to saved media */}
                                            {(unsavedChanges[variation.id]?.image_url || variation.image_url) ? (
                                                <AdDisplay
                                                    campaignId={id || ''}
                                                    adVariationId={variation.id}
                                                    imageUrl={unsavedChanges[variation.id]?.image_url || variation.image_url}
                                                    videoUrl={unsavedChanges[variation.id]?.video_url || variation.video_url}
                                                />
                                            ) : (unsavedChanges[variation.id]?.video_url || variation.video_url) ? (
                                                <AdDisplay
                                                    campaignId={id || ''}
                                                    adVariationId={variation.id}
                                                    imageUrl={unsavedChanges[variation.id]?.image_url || variation.image_url}
                                                    videoUrl={unsavedChanges[variation.id]?.video_url || variation.video_url}
                                                />
                                            ) : (
                                                <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                                    No media
                                                </Box>
                                            )}
                                        </Box>

                                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                            {unsavedChanges[variation.id]?.name || variation.name}
                                        </Typography>

                                        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 1 }}>
                                            {unsavedChanges[variation.id]?.start_time || variation.start_time} - {unsavedChanges[variation.id]?.end_time || variation.end_time}
                                        </Box>

                                        {(unsavedChanges[variation.id]?.start_date || variation.start_date) && 
                                         (unsavedChanges[variation.id]?.end_date || variation.end_date) && (
                                            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 1 }}>
                                                {unsavedChanges[variation.id]?.start_date || variation.start_date} to {unsavedChanges[variation.id]?.end_date || variation.end_date}
                                            </Box>
                                        )}

                                        <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                                                Ad Type: {unsavedChanges[variation.id]?.ad_type || variation.ad_type || 'Not set'}
                                            </Typography>
                                            {(unsavedChanges[variation.id]?.image_url || variation.image_url) && (
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                                    Image URL: {unsavedChanges[variation.id]?.image_url || variation.image_url}
                                                </Typography>
                                            )}
                                            {(unsavedChanges[variation.id]?.video_url || variation.video_url) && (
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                                    Video URL: {unsavedChanges[variation.id]?.video_url || variation.video_url}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Edit Form */}
                        {selectedVariation && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Edit Variation
                                </Typography>
                                {campaign.ad_variations?.map((variation) => {
                                    if (variation.id !== selectedVariation) return null;
                                    
                                    const hasUnsavedChanges = !!unsavedChanges[variation.id];
                                    
                                    return (
                                        <React.Fragment key={variation.id}>
                                            {/* Existing edit form content */}
                                            <Grid container spacing={3}>
                                                <Grid xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Variation Name"
                                                        value={unsavedChanges[variation.id]?.name ?? variation.name}
                                                        onChange={(e) => handleVariationChange(
                                                            variation.id,
                                                            { name: e.target.value }
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid xs={12}>
                                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                        Schedule
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid xs={12} sm={6}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                <TextField
                                                                    label="Start Date"
                                                                    type="date"
                                                                    value={(unsavedChanges[variation.id]?.start_date ?? variation.start_date) || ''}
                                                                    onChange={(e) => handleVariationChange(
                                                                        variation.id,
                                                                        { start_date: e.target.value }
                                                                    )}
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                                <TextField
                                                                    label="Start Time"
                                                                    type="time"
                                                                    value={(unsavedChanges[variation.id]?.start_time ?? variation.start_time) || ''}
                                                                    onChange={(e) => handleVariationChange(
                                                                        variation.id,
                                                                        { start_time: e.target.value }
                                                                    )}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    inputProps={{ step: 300 }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                        <Grid xs={12} sm={6}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                <TextField
                                                                    label="End Date"
                                                                    type="date"
                                                                    value={(unsavedChanges[variation.id]?.end_date ?? variation.end_date) || ''}
                                                                    onChange={(e) => handleVariationChange(
                                                                        variation.id,
                                                                        { end_date: e.target.value }
                                                                    )}
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                                <TextField
                                                                    label="End Time"
                                                                    type="time"
                                                                    value={(unsavedChanges[variation.id]?.end_time ?? variation.end_time) || ''}
                                                                    onChange={(e) => handleVariationChange(
                                                                        variation.id,
                                                                        { end_time: e.target.value }
                                                                    )}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    inputProps={{ step: 300 }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid xs={12}>
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            Media Content
                                                        </Typography>
                                                        <Box sx={{ 
                                                            border: '2px dashed',
                                                            borderColor: 'grey.300',
                                                            borderRadius: 1,
                                                            p: 3,
                                                            mb: 2,
                                                            textAlign: 'center'
                                                        }}>
                                                            {variation.image_url || variation.video_url ? (
                                                                <Box>
                                                                    {variation.image_url ? (
                                                                        <Box
                                                                            component="img"
                                                                            src={variation.image_url}
                                                                            alt={`${variation.name} image`}
                                                                            sx={{
                                                                                width: '100%',
                                                                                height: 'auto',
                                                                                maxHeight: 300,
                                                                                objectFit: 'contain',
                                                                                borderRadius: 1,
                                                                                cursor: 'pointer',
                                                                                mb: 2
                                                                            }}
                                                                            onClick={() => {
                                                                                setShowImageFullscreen(true);
                                                                            }}
                                                                        />
                                                                    ) : variation.video_url ? (
                                                                        <Box
                                                                            sx={{
                                                                                width: '100%',
                                                                                height: 300,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                bgcolor: 'black',
                                                                                borderRadius: 1,
                                                                                cursor: 'pointer',
                                                                                position: 'relative',
                                                                                mb: 2,
                                                                                '&:hover': {
                                                                                    '& .playButton': {
                                                                                        transform: 'scale(1.1)',
                                                                                    }
                                                                                }
                                                                            }}
                                                                            onClick={() => {
                                                                                setShowVideoPlayer(true);
                                                                            }}
                                                                        >
                                                                            <PlayCircleOutline
                                                                                className="playButton"
                                                                                sx={{
                                                                                    fontSize: 60,
                                                                                    color: 'white',
                                                                                    transition: 'transform 0.2s'
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    ) : null}
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="primary"
                                                                        onClick={() => {
                                                                            const updates: Partial<AdVariation> = {
                                                                                image_url: '',
                                                                                video_url: ''
                                                                            };
                                                                            // ad_type will be automatically handled in handleVariationChange
                                                                            handleVariationChange(variation.id, updates);
                                                                        }}
                                                                    >
                                                                        Remove Media
                                                                    </Button>
                                                                </Box>
                                                            ) : (
                                                                <Box>
                                                                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                                                                        Upload an image or video for this variation
                                                                    </Typography>
                                                                    {(unsavedChanges[variation.id]?.image_url || unsavedChanges[variation.id]?.video_url) ? (
                                                                        <Box>
                                                                            {unsavedChanges[variation.id]?.image_url ? (
                                                                                <Box
                                                                                    component="img"
                                                                                    src={unsavedChanges[variation.id]?.image_url}
                                                                                    alt={`${variation.name} preview`}
                                                                                    sx={{
                                                                                        width: '100%',
                                                                                        height: 'auto',
                                                                                        maxHeight: 300,
                                                                                        objectFit: 'contain',
                                                                                        borderRadius: 1,
                                                                                        mb: 2
                                                                                    }}
                                                                                />
                                                                            ) : unsavedChanges[variation.id]?.video_url ? (
                                                                                <Box sx={{ mb: 2 }}>
                                                                                    <video
                                                                                        src={unsavedChanges[variation.id]?.video_url}
                                                                                        style={{
                                                                                            width: '100%',
                                                                                            maxHeight: 300,
                                                                                            borderRadius: '4px'
                                                                                        }}
                                                                                        controls
                                                                                    />
                                                                                </Box>
                                                                            ) : null}
                                                                            <Button
                                                                                variant="outlined"
                                                                                color="primary"
                                                                                onClick={() => {
                                                                                    const updates: Partial<AdVariation> = {
                                                                                        image_url: '',
                                                                                        video_url: ''
                                                                                    };
                                                                                    // ad_type will be automatically handled in handleVariationChange
                                                                                    handleVariationChange(variation.id, updates);
                                                                                }}
                                                                            >
                                                                                Remove Media
                                                                            </Button>
                                                                        </Box>
                                                                    ) : (
                                                                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                                                            <ImageUpload
                                                                                campaignId={id || ''}
                                                                                onUploadComplete={(url) => handleVariationChange(
                                                                                    variation.id,
                                                                                    { 
                                                                                        image_url: url,
                                                                                        video_url: '', // Clear video when image is uploaded
                                                                                        ad_type: 'image' // Set ad_type to image
                                                                                    }
                                                                                )}
                                                                            />
                                                                            <Typography color="text.secondary" sx={{ mx: 2 }}>
                                                                                or
                                                                            </Typography>
                                                                            <VideoUpload
                                                                                campaignId={id || ''}
                                                                                onUploadComplete={(url) => handleVariationChange(
                                                                                    variation.id,
                                                                                    { 
                                                                                        video_url: url,
                                                                                        image_url: '', // Clear image when video is uploaded
                                                                                        ad_type: 'video' // Set ad_type to video
                                                                                    }
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid xs={12}>
                                                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={() => handleDeleteVariation(variation.id)}
                                                        >
                                                            Delete Variation
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => handleSaveVariation(variation.id)}
                                                            disabled={!hasUnsavedChanges || isSaving}
                                                            startIcon={isSaving ? <CircularProgress size={20} /> : null}
                                                        >
                                                            {isSaving ? 'Saving...' : 'Save Changes'}
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </React.Fragment>
                                    );
                                })}
                            </Paper>
                        )}
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Campaign Analytics
                        </Typography>

                        {analyticsLoading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress />
                            </Box>
                        ) : analyticsError ? (
                            <Alert severity="error">{analyticsError}</Alert>
                        ) : !analytics ? (
                            <Typography color="text.secondary" align="center">
                                No analytics data available yet.
                            </Typography>
                        ) : (
                            <>
                                {/* Select Analytics Target */}
                                <Box sx={{ mb: 3 }}>
                                    <FormControl sx={{ minWidth: 220 }}>
                                        <InputLabel>Analytics Target</InputLabel>
                                        <Select
                                            value={analyticsTarget}
                                            label="Analytics Target"
                                            onChange={(e) => setAnalyticsTarget(e.target.value)}
                                        >
                                            <MenuItem value="campaign">Campaign Overall</MenuItem>
                                            {campaign?.ad_variations?.map((v) => (
                                                <MenuItem key={v.id} value={v.id}>{v.name || v.id}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Overall Summary Cards */}
                                {(() => {
                                    const metrics: any = analyticsTarget === 'campaign' ? analytics : analytics?.variations?.[analyticsTarget];
                                    if (!metrics) return null;
                                    return (
                                        <Grid container spacing={2} sx={{ mb: 4 }}>
                                            <Grid xs={12} sm={6} md={3}>
                                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                                    <Typography color="text.secondary" variant="subtitle2">
                                                        Total Impressions
                                                    </Typography>
                                                    <Typography variant="h4">
                                                        {metrics.totalImpressions || 0}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid xs={12} sm={6} md={3}>
                                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                                    <Typography color="text.secondary" variant="subtitle2">
                                                        Total Clicks
                                                    </Typography>
                                                    <Typography variant="h4">
                                                        {metrics.totalClicks || 0}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid xs={12} sm={6} md={3}>
                                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                                    <Typography color="text.secondary" variant="subtitle2">
                                                        Click Through Rate
                                                    </Typography>
                                                    <Typography variant="h4">
                                                        {metrics.totalImpressions ? ((metrics.totalClicks / metrics.totalImpressions) * 100).toFixed(1) : '0.0'}%
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid xs={12} sm={6} md={3}>
                                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                                    <Typography color="text.secondary" variant="subtitle2">
                                                        Avg View Duration
                                                    </Typography>
                                                    <Typography variant="h4">
                                                        {(metrics.avgViewDuration || 0).toFixed(1)}s
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        </Grid>
                                    );
                                })()}

                                {/* Time Period Navigation */}
                                <Paper sx={{ p: 3, mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Time Period View
                                    </Typography>
                                    
                                    {/* Time Range Selector */}
                                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <FormControl sx={{ minWidth: 150 }}>
                                            <InputLabel>Time Range</InputLabel>
                                            <Select
                                                value={timeRange}
                                                onChange={(e) => setTimeRange(e.target.value)}
                                                label="Time Range"
                                            >
                                                <MenuItem value="daily">Daily</MenuItem>
                                                <MenuItem value="weekly">Weekly</MenuItem>
                                                <MenuItem value="monthly">Monthly</MenuItem>
                                            </Select>
                                        </FormControl>
                                        
                                        <Button
                                            startIcon={<Today />}
                                            onClick={goToToday}
                                            variant="outlined"
                                            size="small"
                                        >
                                            Today
                                        </Button>
                                        
                                        <Button
                                            startIcon={<CalendarMonth />}
                                            onClick={() => setShowDatePicker(!showDatePicker)}
                                            variant="outlined"
                                            size="small"
                                        >
                                            Select Date
                                        </Button>
                                    </Box>

                                    {/* Date Picker */}
                                    {showDatePicker && (
                                        <Box sx={{ mb: 3 }}>
                                            <TextField
                                                type="date"
                                                label="Select Date"
                                                value={selectedDate.toISOString().split('T')[0]}
                                                onChange={(e) => {
                                                    // Create date object from the selected date string
                                                    const [year, month, day] = e.target.value.split('-').map(Number);
                                                    const newDate = new Date(year, month - 1, day); // month is 0-indexed
                                                    // Set time to start of day in local timezone
                                                    newDate.setHours(0, 0, 0, 0);
                                                    setSelectedDate(newDate);
                                                    setShowDatePicker(false);
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ mr: 2 }}
                                            />
                                        </Box>
                                    )}

                                    {/* Date Navigation */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                                        <IconButton 
                                            onClick={() => navigateDate('prev')}
                                            sx={{ color: 'primary.main' }}
                                        >
                                            <ChevronLeft />
                                        </IconButton>
                                        
                                        <Chip 
                                            label={formatDisplayDate(selectedDate, timeRange)}
                                            color="primary" 
                                            variant="outlined"
                                            sx={{ minWidth: 200, fontSize: '0.9rem', height: 36 }}
                                        />
                                        
                                        <IconButton 
                                            onClick={() => navigateDate('next')}
                                            sx={{ color: 'primary.main' }}
                                        >
                                            <ChevronRight />
                                        </IconButton>
                                    </Box>

                                    {/* Selected Period Metrics */}
                                    {(() => {
                                        const metrics: any = analyticsTarget === 'campaign' ? analytics : analytics?.variations?.[analyticsTarget];
                                        const periodMetrics = getMetricsForSelectedPeriod(metrics);
                                        if (!periodMetrics) return null;
                                        
                                        return (
                                            <Box>
                                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                    Metrics for {formatDisplayDate(selectedDate, timeRange)}
                                                </Typography>
                                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                                    <Grid xs={12} sm={4}>
                                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: (theme) => theme.palette.primary.main + '20' }}>
                                                            <Typography color="primary" variant="subtitle2">
                                                                Impressions
                                                            </Typography>
                                                            <Typography variant="h5" color="primary">
                                                                {periodMetrics.impressions || 0}
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>
                                                    <Grid xs={12} sm={4}>
                                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: (theme) => theme.palette.success.main + '20' }}>
                                                            <Typography color="success.main" variant="subtitle2">
                                                                Clicks
                                                            </Typography>
                                                            <Typography variant="h5" color="success.main">
                                                                {periodMetrics.clicks || 0}
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>
                                                    <Grid xs={12} sm={4}>
                                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: (theme) => theme.palette.warning.main + '20' }}>
                                                            <Typography color="warning.main" variant="subtitle2">
                                                                Avg View Duration
                                                            </Typography>
                                                            <Typography variant="h5" color="warning.main">
                                                                {(periodMetrics.viewDuration || 0).toFixed(1)}s
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>
                                                </Grid>
                                                
                                                {/* CTR for the period */}
                                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: (theme) => theme.palette.info.main + '20' }}>
                                                    <Typography color="info.main" variant="subtitle2">
                                                        Click Through Rate
                                                    </Typography>
                                                    <Typography variant="h5" color="info.main">
                                                        {periodMetrics.impressions ? ((periodMetrics.clicks / periodMetrics.impressions) * 100).toFixed(1) : '0.0'}%
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        );
                                    })()}
                                </Paper>

                                {/* Time-based Metrics Chart */}
                                {(() => {
                                    const metrics: any = analyticsTarget === 'campaign' ? analytics : analytics?.variations?.[analyticsTarget];
                                    if (!metrics) return null;
                                    const source = timeRange === 'daily' ? metrics.dailyMetrics || {} :
                                                  timeRange === 'weekly' ? metrics.weeklyMetrics || {} :
                                                  metrics.monthlyMetrics || {};
                                    
                                    // Sort dates and take last 30 entries for better visualization
                                    const sortedData = Object.entries(source)
                                        .sort(([a], [b]) => a.localeCompare(b))
                                        .slice(-30)
                                        .map(([date, m]: any) => ({
                                            date: new Date(date).toLocaleDateString('en-US', { 
                                                month: 'short', 
                                                day: 'numeric',
                                                ...(timeRange === 'monthly' && { year: 'numeric' })
                                            }),
                                            fullDate: date,
                                            impressions: m.impressions || 0,
                                            clicks: m.clicks || 0,
                                            viewDuration: m.viewDuration || 0
                                        }));
                                    
                                    return (
                                        <Paper sx={{ p: 3, mb: 4 }}>
                                            <Typography variant="h6" gutterBottom>
                                                Performance Trend
                                            </Typography>
                                            
                                            {sortedData.length === 0 ? (
                                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography color="text.secondary">
                                                        No data available for the selected time range
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ height: 350 }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart data={sortedData}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis 
                                                                dataKey="date" 
                                                                angle={-45}
                                                                textAnchor="end"
                                                                height={60}
                                                                fontSize={12}
                                                            />
                                                            <YAxis yAxisId="left" />
                                                            <YAxis yAxisId="right" orientation="right" />
                                                            <Tooltip 
                                                                labelFormatter={(label, payload) => {
                                                                    if (payload && payload[0]) {
                                                                        const data = payload[0].payload;
                                                                        return `${label} (${data.fullDate})`;
                                                                    }
                                                                    return label;
                                                                }}
                                                            />
                                                            <Legend />
                                                            <Line yAxisId="left" type="monotone" dataKey="impressions" stroke="#8884d8" name="Impressions" strokeWidth={2} />
                                                            <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="#82ca9d" name="Clicks" strokeWidth={2} />
                                                            <Line yAxisId="right" type="monotone" dataKey="viewDuration" stroke="#ffc658" name="Avg View Duration (s)" strokeWidth={2} />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </Box>
                                            )}
                                        </Paper>
                                    );
                                })()}

                            
                            </>
                        )}
                    </Box>
                </TabPanel>
            </Paper>

            {/* Video Player Dialog */}
            <Dialog
                fullScreen
                open={showVideoPlayer}
                onClose={() => setShowVideoPlayer(false)}
            >
                <Box sx={{ 
                    bgcolor: 'black', 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column' 
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        p: 2, 
                        bgcolor: 'rgba(0,0,0,0.8)' 
                    }}>
                        <Button 
                            startIcon={<ArrowBack />} 
                            color="inherit" 
                            onClick={() => setShowVideoPlayer(false)}
                        >
                            Back
                        </Button>
                        <IconButton 
                            color="inherit" 
                            onClick={() => setShowVideoPlayer(false)}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                    <Box sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                    }}>
                        {selectedVariation && campaign.ad_variations?.map(variation => {
                            if (variation.id !== selectedVariation) return null;
                            const videoUrl = unsavedChanges[variation.id]?.video_url || variation.video_url;
                            return videoUrl ? (
                                <video
                                    key={variation.id}
                                    controls
                                    autoPlay
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '90vh',
                                    }}
                                    src={videoUrl}
                                />
                            ) : null;
                        })}
                    </Box>
                </Box>
            </Dialog>

            {/* Full Screen Image Dialog */}
            <Dialog 
                fullScreen 
                open={showImageFullscreen} 
                onClose={() => setShowImageFullscreen(false)}
            >
                <Box sx={{ 
                    bgcolor: 'black', 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column' 
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        p: 2, 
                        bgcolor: 'rgba(0,0,0,0.8)' 
                    }}>
                        <Button 
                            startIcon={<ArrowBack />} 
                            color="inherit" 
                            onClick={() => setShowImageFullscreen(false)}
                        >
                            Back
                        </Button>
                        <IconButton 
                            color="inherit" 
                            onClick={() => setShowImageFullscreen(false)}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                    <Box sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                    }}>
                        {selectedVariation && campaign.ad_variations?.map(variation => {
                            if (variation.id !== selectedVariation) return null;
                            return variation.image_url ? (
                                <Box 
                                    key={variation.id}
                                    component="img"
                                    src={variation.image_url}
                                    alt={variation.name || 'Campaign variation image'}
                                    sx={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '90vh', 
                                        objectFit: 'contain' 
                                    }}
                                />
                            ) : null;
                        })}
                    </Box>
                </Box>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this ad variation? 
                        <strong> This action cannot be undone and will permanently delete all analytics data associated with this variation.</strong>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        color="error" 
                        variant="contained" 
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CampaignDetailView; 